import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Paper, Typography, useTheme} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {Dayjs} from 'dayjs';

import {CalendarItem, groupByDay, monthGridDays, timeLabel} from './calendarModel';

/** Wie viele Termine ein Tagesfeld zeigt, bevor auf „+n" zusammengefasst wird. */
const MAX_PER_DAY = 3;

interface Props {
    anchor: Dayjs;
    items: CalendarItem[];
    now: Dayjs;
    onSelect: (item: CalendarItem) => void;
    /** Wechselt in die Wochenansicht der angeklickten Woche. */
    onPickDay: (day: Dayjs) => void;
}

/**
 * Monatsansicht als klassisches Raster: sieben Spalten, volle Wochen von Montag bis Sonntag.
 *
 * Die Tage der Nachbarmonate werden mitgezeichnet, aber blass — sonst rutschen die Wochentage in
 * den Zeilen gegeneinander und das Raster verliert genau die Eigenschaft, für die man es benutzt.
 */
export const MonthGrid: React.FC<Props> = ({anchor, items, now, onSelect, onPickDay}) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();

    const days = useMemo(() => monthGridDays(anchor), [anchor]);
    const byDay = useMemo(() => groupByDay(items), [items]);
    const todayKey = now.format('YYYY-MM-DD');
    const month = anchor.month();

    const headers = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(i18n.language, {weekday: 'short'});
        return days.slice(0, 7).map((day) => formatter.format(day.toDate()));
    }, [days, i18n.language]);

    return (
        <Paper variant="outlined" sx={{overflow: 'hidden'}}>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))'}}>
                {headers.map((label, index) => (
                    <Box key={label + index} sx={{
                        py: 0.75,
                        textAlign: 'center',
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: index >= 5 ? 'action.hover' : undefined,
                    }}>
                        <Typography variant="caption" color="text.secondary"
                                    sx={{textTransform: 'uppercase'}}>
                            {label}
                        </Typography>
                    </Box>
                ))}

                {days.map((day) => {
                    const key = day.format('YYYY-MM-DD');
                    const dayItems = byDay.get(key) ?? [];
                    const isToday = key === todayKey;
                    const isOtherMonth = day.month() !== month;
                    const isWeekend = day.day() === 0 || day.day() === 6;
                    const shown = dayItems.slice(0, MAX_PER_DAY);
                    const hidden = dayItems.length - shown.length;

                    return (
                        <Box key={day.valueOf()} onClick={() => onPickDay(day)} sx={{
                            minHeight: {xs: 84, md: 116},
                            p: 0.5,
                            cursor: 'pointer',
                            borderTop: 1,
                            borderLeft: 1,
                            borderColor: 'divider',
                            // Heute schlaegt Wochenende: an einem Samstag soll die Tönung den
                            // heutigen Tag zeigen, nicht den Wochenendcharakter.
                            bgcolor: isToday
                                ? alpha(theme.palette.primary.main, 0.08)
                                : isWeekend ? 'action.hover' : undefined,
                            // Rahmen als innerer Schatten statt als Border: eine echte Border wuerde
                            // die Zelle im Raster um zwei Pixel verschieben und die Spalten brechen.
                            boxShadow: isToday
                                ? `inset 0 0 0 2px ${theme.palette.primary.main}`
                                : undefined,
                            opacity: isOtherMonth ? 0.45 : 1,
                            '&:hover': {bgcolor: 'action.selected'},
                        }}>
                            <Typography variant="caption" sx={{
                                display: 'inline-block',
                                fontWeight: isToday ? 700 : 500,
                                color: isToday ? 'primary.contrastText' : 'text.secondary',
                                bgcolor: isToday ? 'primary.main' : 'transparent',
                                borderRadius: '50%',
                                minWidth: 20,
                                height: 20,
                                lineHeight: '20px',
                                textAlign: 'center',
                                mb: 0.25,
                            }}>
                                {day.date()}
                            </Typography>

                            {shown.map((item) => (
                                <Box key={item.key}
                                     onClick={(e) => {
                                         e.stopPropagation();
                                         onSelect(item);
                                     }}
                                     sx={{
                                         display: 'flex',
                                         alignItems: 'center',
                                         gap: 0.5,
                                         px: 0.5,
                                         mb: 0.25,
                                         borderRadius: 0.5,
                                         bgcolor: alpha(theme.palette[item.color].main, 0.16),
                                         borderLeft: 2,
                                         borderColor: `${item.color}.main`,
                                     }}>
                                    {!item.allDay && (
                                        <Typography variant="caption" color="text.secondary"
                                                    sx={{flexShrink: 0, fontVariantNumeric: 'tabular-nums'}}>
                                            {timeLabel(item, '').split('–')[0]}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            ))}

                            {hidden > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{pl: 0.5}}>
                                    {t('calendar.more', {count: hidden})}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default MonthGrid;
